import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isBreakGlassAdmin } from '@/shared/auth/scopes.js';
import baseAxios from '@/shared/utils/request';
import { MY_LANGUAGE } from '@/shared/utils/urls';

// ─────────────────────────────────────────────────────────────
// Tell docs-api which language to write to this person in (PUT
// /api/v1/users/me/language: the mail and documents it composes later, with
// no browser to ask).
//
// The person picks the language in the install's top bar (the zehn_prefs
// cookie every module shares, @zehnmindai/embed's PrefsProvider), possibly in
// another module; i18next follows it here. So this listens to i18next's
// `languageChanged`, whatever the source, and also says what this page shows
// when it starts (a choice made elsewhere while docs was closed). docs-api
// keeps en, ru or uz; both Uzbek scripts are "uz" there. What docs-api
// already has (the person's `language`) is not written again. The
// break-glass admin has no language at docs-api. Best effort: a failure is
// tried again on the next change, and says nothing.
// ─────────────────────────────────────────────────────────────

const API_LANGUAGES = new Set(['en', 'ru', 'uz']);

/** docs-api's language for a UI language: "uz-Cyrl" → "uz", "en-GB" → "en". */
export const apiLanguageOf = (code) =>
  String(code || '')
    .split('-')[0]
    .toLowerCase();

export function useLanguageSync(user) {
  const { i18n } = useTranslation();
  const sent = useRef(null);
  const uuid = user?.uuid;
  const admin = isBreakGlassAdmin(user);
  const known = apiLanguageOf(user?.language);

  useEffect(() => {
    if (!uuid || admin) return undefined;
    sent.current = known || null;
    const push = (raw) => {
      const language = apiLanguageOf(raw);
      if (!API_LANGUAGES.has(language) || sent.current === language) return;
      sent.current = language;
      baseAxios({ method: 'PUT', url: MY_LANGUAGE, data: { language } }).catch(() => {});
    };
    push(i18n.resolvedLanguage || i18n.language);
    i18n.on('languageChanged', push);
    return () => i18n.off('languageChanged', push);
    // Once per person: react-i18next hands out a new `i18n` reference on a
    // language change, and starting again would forget what was written.
    // `known` is read when the person changes, not on every answer from /me.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, admin]);
}

export default useLanguageSync;
